/* eslint-disable @typescript-eslint/indent */
/* eslint-disable @typescript-eslint/naming-convention */
import { TitleCasePipe } from "@angular/common";
import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty } from "lodash";
import { monkeyPatchChartJsLegend, monkeyPatchChartJsTooltip } from "ng2-charts";
import { map } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { AuthenticationService } from "../../../core/authentication/authentication.service";
import { PaginatedResponse } from "../../../core/http/paginated-response.type";
import { QueryParams } from "../../../core/http/query-params.type";
import { Card, CardTable } from "../../../shared/components/card/card.type";
import { DataTableComponent } from "../../../shared/components/data-table/data-table.component";
import { DataTableParams } from "../../../shared/components/data-table/data-table.type";
import { DynamicFormComponent } from "../../../shared/components/dynamic-form/dynamic-form.component";
import { MapBoxComponent } from "../../../shared/components/map-box/map-box.component";
import { MapBoxService } from "../../../shared/components/map-box/map-box.service";
import { MapBoxData, MapBoxLocation } from "../../../shared/components/map-box/map-box.type";
import { PieChartComponent } from "../../../shared/components/pie-chart/pie-chart.component";
import { PieChartData } from "../../../shared/components/pie-chart/pie-chart.type";
import { PAGINATOR } from "../../../shared/constants/components";
import { FORMAT } from "../../../shared/constants/formats";
import { MomentPipe } from "../../../shared/pipes/moment/moment.pipe";
import { AnyObject } from "../../../shared/types/app.type";
import { UserInfo } from "../../../shared/types/user-info";
import { removePropsWithKeys } from "../../../shared/utils/objects/object";
import { getAssetCoverageStatuses, getAssetSearchFilterOptions } from "../assets/asset";
import { AssetService } from "../assets/asset.service";
import { Asset, AssetCountByCoverageStatus, AssetCoverageStatuses } from "../assets/asset.type";
import { CompanyService } from "../companies/company.service";
import { contractEndDates, getContractSearchFilterOptions } from "../contracts/contract";
import { ContractService } from "../contracts/contract.service";
import { Contract, ContractCountByEndDate } from "../contracts/contract.type";
import { LocationService } from "../locations/location.service";
import { Location } from "../locations/location.type";
import { PartnerService } from "../partners/partner.service";
import { getTicketSearchFilterOptions, ticketPriorities, ticketStatuses } from "../tickets/ticket";
import { TicketService } from "../tickets/ticket.service";
import { Ticket, TicketCountByStatus, TicketStatusReport } from "../tickets/ticket.type";
import { DashboardService } from "./dashboard.service";

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
  providers: [TitleCasePipe, DashboardService],
})
export class DashboardComponent implements OnInit {
  @ViewChild(DynamicFormComponent) dynamicForm: DynamicFormComponent;
  currentUser: UserInfo;
  cards: {
    contracts: Card<DataTableComponent<Contract>, CardTable<Contract>>;
    assets: Card<DataTableComponent<Asset>, CardTable<Asset>>;
    tickets: Card<DataTableComponent<Ticket>, CardTable<Ticket>>;
    ticketsReport: Card<DataTableComponent<TicketStatusReport & { id?: string }>, CardTable<TicketStatusReport>>;
    contractsChart: Card<PieChartComponent, PieChartData>;
    assetsChart: Card<PieChartComponent, PieChartData>;
    locationsMap: Card<MapBoxComponent<Location>, MapBoxData<Location>>;
  };

  constructor(
    public mapBoxService: MapBoxService,
    private companyService: CompanyService,
    private partnerService: PartnerService,
    private ticketService: TicketService,
    private locationService: LocationService,
    private assetService: AssetService,
    private authenticationService: AuthenticationService,
    private contractService: ContractService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.currentUser = this.authenticationService.currentUserInfo;

    monkeyPatchChartJsTooltip();
    monkeyPatchChartJsLegend();
  }

  ngOnInit(): void {
    this.initContractsCard();
    this.initAssetsCard();
    this.initTicketsCard();
    this.initContractChartCard();
    this.initAssetChartCard();
    this.initTicketsReportCard();
    this.initLocationCard();
  }

  // #region Private Methods

  private initContractsCard(): void {
    this.cards = {
      ...this.cards,
      contracts: {
        title: "Contracts",
        path: "/contracts",
        addButton: {
          title: "Add Contract",
          path: "/contracts/create",
          hidden: this.currentUser.hasRight(["viewer"]),
        },
        component: DataTableComponent,
        data: {
          dataTable: {
            id: "contracts",
            entityName: null,
            bodyHeight: 360,
            boxShadow: false,
            scrollbarH: false,
            paginator: {
              labelHidden: true,
              pageSizeOptions: [10, ...PAGINATOR.PAGE_SIZE_OPTIONS],
            },
            toolBar: {
              hidden: true,
            },
            columns: [
              {
                prop: "name",
                name: "Name",
              },
              {
                prop: "number",
                name: "Contract #",
                render: (contract) => contract.numberVersion,
                link: {
                  path: (contract) => `/contracts/${contract.id}`,
                },
              },
              {
                prop: "endDate",
                name: "End Date",
                pipes: [{ clazz: MomentPipe, args: [FORMAT.DATE] }],
              },
            ],
            searchBar: [
              {
                availableSearchFields: getContractSearchFilterOptions(this.currentUser, {
                  shown: ["name", "endDate", "number"],
                }),
              },
            ],
          },
          getDataTable: (
            onResult: (data: PaginatedResponse<Contract>) => void,
            onError: (error: string) => void,
            params: DataTableParams<Contract>,
          ): void => {
            const queryParams: QueryParams<Contract> = {
              ...params,
              relations: { maintenanceProvider: true, company: { partner: true } },
              where: { ...removePropsWithKeys<Contract>(params.where, ["partner.name", "company.name"]) },
              order: isEmpty(params.order) ? { endDate: "desc" } : params.order,
            };
            this.contractService.getContracts(queryParams).subscribe(
              (data) => {
                onResult(data);
              },
              (error) => {
                onError(error);
              },
            );
          },
        },
      },
    };
  }

  private initAssetsCard(): void {
    this.cards = {
      ...this.cards,
      assets: {
        title: "Assets",
        path: "/assets",
        addButton: {
          title: "Add Asset",
          path: "/assets/create",
          hidden: this.currentUser.hasRight(["viewer"]),
        },
        component: DataTableComponent,
        data: {
          dataTable: {
            id: "assets",
            entityName: null,
            bodyHeight: 360,
            boxShadow: false,
            scrollbarH: false,
            paginator: {
              labelHidden: true,
              pageSizeOptions: [10, ...PAGINATOR.PAGE_SIZE_OPTIONS],
            },
            toolBar: {
              hidden: true,
            },
            columns: [
              {
                prop: "name",
                name: "Name",
              },
              {
                prop: "serialNumber",
                name: "Serial #",
                link: {
                  path: (asset) => `/assets/${asset.id}`,
                },
              },
            ],
            searchBar: [
              {
                availableSearchFields: getAssetSearchFilterOptions(this.currentUser, {
                  shown: ["name", "serialNumber", "category"],
                }),
                defaultOption: "category",
              },
            ],
          },
          getDataTable: (
            onResult: (data: PaginatedResponse<Asset>) => void,
            onError: (error: string) => void,
            params: DataTableParams<Asset>,
          ): void => {
            const queryParams: QueryParams<Asset> = {
              ...params,
              where: { ...params.where },
              order: isEmpty(params.order) ? { id: "desc" } : params.order,
            };
            this.assetService.getAssets(queryParams).subscribe(
              (result) => {
                onResult(result);
              },
              (error) => {
                onError(error);
              },
            );
          },
        },
      },
    };
  }

  private initTicketsCard(): void {
    this.cards = {
      ...this.cards,
      tickets: {
        title: "Tickets",
        path: "/tickets",
        addButton: {
          title: "Add Ticket",
          path: "/tickets/create",
        },
        component: DataTableComponent,
        data: {
          dataTable: {
            id: "tickets",
            entityName: null,
            bodyHeight: 360,
            boxShadow: false,
            scrollbarH: false,
            paginator: {
              labelHidden: true,
              pageSizeOptions: [10, ...PAGINATOR.PAGE_SIZE_OPTIONS],
            },
            toolBar: {
              hidden: true,
            },
            columns: [
              {
                prop: "zendeskId",
                name: "ID",
                render: (row) => row.zendeskId,
                link: {
                  path: (ticket) => `/tickets/${ticket.id}`,
                },
                maxWidth: 75,
                minWidth: 75,
              },
              {
                prop: "priority",
                name: "Priority",
                render: (row) => ticketPriorities.find((p) => p.value === row.priority)?.label,
                maxWidth: 150,
                minWidth: 150,
              },
              {
                prop: "subject",
                name: "Subject",
              },
            ],
            searchBar: [
              {
                availableSearchFields: getTicketSearchFilterOptions(this.currentUser, {
                  shown: ["zendeskId", "priority", "subject", "asset.company.name"],
                  companyService: this.companyService,
                  partnerService: this.partnerService,
                }),
                defaultOption: "asset.company.name",
              },
            ],
          },
          getDataTable: (
            onResult: (data: PaginatedResponse<Ticket>) => void,
            onError: (error: string) => void,
            params: DataTableParams,
          ): void => {
            this.ticketService
              .getTickets({
                ...params,
                where: {
                  ...removePropsWithKeys<Ticket>(params.where, ["company.name"]),
                  assetId: { $not: null },
                },
                relations: { asset: { company: true } },
                order: isEmpty(params.order) ? { zendeskId: "desc" } : params.order,
              })
              .subscribe(
                (data): void => {
                  onResult(data);
                },
                (error): void => {
                  onError(error);
                },
              );
          },
        },
      },
    };
  }

  private initContractChartCard(): void {
    this.cards = {
      ...this.cards,
      contractsChart: {
        title: "Days until Contract Renewal",
        path: "/contracts",
        component: PieChartComponent,
        data: {
          id: "Contract Chart",
          columns: 2,
          data: [],
          plugins: [],
          colors: [],
          chartType: "pie",
          legend: false,
          bodyHeight: 180,
          queryParamsDetails: {
            alias: "contracts",
            labels: contractEndDates,
            filter: "endDate",
          },
          options: {
            legend: {
              display: true,
              labels: {
                usePointStyle: true,
              },
              position: "left",
            },
            plugins: {
              datalabels: {
                formatter: (_, ctx) => ctx.chart.data.labels[ctx.dataIndex],
              },
            },
            tooltips: {
              enabled: true,
            },
            onClick: (_, item: AnyObject[]) => {
              if (item[0]) {
                const label = item[0]._model.label;
                const value = contractEndDates.find((opt) => opt.label === label).value;
                const filter = "endDate";
                this.router.navigate(["/contracts"], {
                  queryParams: {
                    contracts_0_value: value,
                    contracts_0_filter: filter,
                  },
                });
              }
            },
            onHover: (event: any, item: AnyObject[]) => {
              event.target.style.cursor = item[0] ? "pointer" : "default";
            },
            aspectRatio: 1,
          },
        },
      },
    };

    const key = "representativeId";
    const representativeId = this.route.snapshot.queryParamMap.get(key);
    this.updateContractsChart(representativeId);
  }

  private initAssetChartCard(): void {
    this.cards = {
      ...this.cards,
      assetsChart: {
        title: "Asset Contractual Status",
        path: "/assets",
        component: PieChartComponent,
        data: {
          id: "Asset Chart",
          columns: 3,
          data: [],
          plugins: [],
          colors: [],
          chartType: "pie",
          legend: false,
          bodyHeight: 180,
          queryParamsDetails: {
            alias: "assets",
            labels: getAssetCoverageStatuses(),
            filter: "coverageStatus",
          },
          options: {
            legend: {
              display: true,
              labels: {
                usePointStyle: true,
              },
              position: "left",
            },
            plugins: {
              datalabels: {
                formatter: (_, ctx) => ctx.chart.data.labels[ctx.dataIndex],
              },
            },
            tooltips: {
              enabled: true,
            },
            onClick: (_, item: AnyObject[]) => {
              if (item[0]) {
                const label = item[0]._model.label;
                const value = getAssetCoverageStatuses().find((opt) => opt.label === label).value;
                const filter = "coverageStatus";

                this.router.navigate(["/assets"], {
                  queryParams: {
                    assets_0_value: value,
                    assets_0_filter: filter,
                  },
                });
              }
            },
            onHover: (event: any, item: AnyObject[]) => {
              event.target.style.cursor = item[0] ? "pointer" : "default";
            },
            aspectRatio: 1,
          },
        },
      },
    };

    this.updateAssetsChart();
  }

  private initTicketsReportCard(): void {
    this.cards = {
      ...this.cards,
      ticketsReport: {
        title: "Tickets Report",
        path: "/tickets",
        component: DataTableComponent,
        data: {
          dataTable: {
            id: "tickets-report",
            entityName: null,
            bodyHeight: this.currentUser.isCompanyOrViewAsCompany() ? 348 : 360,
            boxShadow: false,
            scrollbarH: false,
            paginator: {
              pageSizeOptions: [10, ...PAGINATOR.PAGE_SIZE_OPTIONS],
              disabled: true,
            },
            toolBar: {
              hidden: true,
            },
            columns: [
              {
                prop: "status",
                name: "Status",
                render: (row) => ticketStatuses.find((p) => p.value === row.status)?.label,
              },
              {
                prop: "count",
                name: "Count",
              },
            ],
            searchBar: [
              {
                availableSearchFields: getTicketSearchFilterOptions(this.currentUser, {
                  shown: ["company.name"],
                  companyService: this.companyService,
                  partnerService: this.partnerService,
                }),
                defaultOption: "company.name",
                hidden: this.currentUser.isCompanyOrViewAsCompany(),
              },
            ],
          },
          getDataTable: (
            onResult: (data: PaginatedResponse<TicketStatusReport>) => void,
            onError: (error: string) => void,
            params: DataTableParams,
          ): void => {
            this.ticketService
              .getTicketCountByStatus(params)
              .pipe(
                map<TicketCountByStatus, TicketStatusReport[]>((result) =>
                  Object.keys(result).map<TicketStatusReport>((k) => ({ status: k, count: result[k] })),
                ),
              )
              .subscribe(
                (data): void => {
                  const paginatedData: PaginatedResponse<TicketStatusReport> = {
                    items: [...data],
                    meta: { currentPage: 1, itemCount: 5, itemsPerPage: 5, totalItems: 5, totalPages: 1 },
                  };
                  onResult(paginatedData);
                },
                (error): void => {
                  onError(error);
                },
              );
          },
        },
      },
    };
  }

  private initLocationCard(): void {
    this.cards.locationsMap = {
      title: "Locations",
      path: "/locations",
      component: MapBoxComponent,
      data: {
        dataSource: {
          items: [],
          meta: {
            currentPage: 1,
            itemCount: 0,
            itemsPerPage: 0,
            totalItems: 0,
            totalPages: 0,
          },
        },
        updateLocations: (params: QueryParams<Location>) =>
          this.updateLocations({
            pagination: { page: 1, pageSize: environment.mapboxLocationPageSize, ...params?.pagination },
          }),
      },
    };
  }

  private updateContractsChart(representativeId?: string): void {
    const data: PieChartData["data"] = [
      {
        id: "late",
        key: "late",
        label: "Expired",
        value: 0,
        colors: {
          background: "rgba(214, 93, 93, 1)",
          foreground: "white",
        },
      },
      {
        id: "now",
        key: "now",
        label: "Now",
        value: 0,
        colors: {
          background: "rgba(242, 196, 12, 1)",
        },
      },
      {
        id: "plus30",
        key: "plus30",
        label: "+30 days",
        value: 0,
        colors: {
          background: "rgba(160, 210, 42, 1)",
          foreground: "white",
        },
      },
      {
        id: "plus90",
        key: "plus90",
        label: "+90 days",
        value: 0,
        colors: {
          background: "rgba(110, 196, 196, 1)",
          foreground: "white",
        },
      },
    ];

    this.contractService.getCountByEndDate(representativeId).subscribe(
      (count: ContractCountByEndDate) => {
        this.cards = {
          ...this.cards,
          contractsChart: {
            ...this.cards.contractsChart,
            data: {
              ...this.cards.contractsChart.data,
              data: data.map((d) => ({
                ...d,
                value: count[d.key],
              })),
              colors: [
                {
                  backgroundColor: data.map((d) => d.colors.background),
                },
              ],
            },
          },
        };
      },
      () => {
        this.cards = {
          ...this.cards,
          contractsChart: {
            ...this.cards.contractsChart,
            data: {
              ...this.cards.contractsChart.data,
              data,
            },
          },
        };
      },
    );
  }

  private updateAssetsChart(): void {
    const data: PieChartData["data"] = [
      {
        id: "pending",
        key: "pending",
        label: "Pending",
        value: 0,
        colors: {
          background: "rgba(184, 138, 214, 1)",
          foreground: "white",
        },
      },
      {
        id: "active",
        key: "active",
        label: "Active",
        value: 0,
        colors: {
          background: "rgba(160, 210, 42, 1)",
          foreground: "white",
        },
      },
      {
        id: "terminated",
        key: "terminated",
        label: "Terminated",
        value: 0,
        colors: {
          background: "rgba(242, 196, 12, 1)",
        },
      },
      {
        id: "not covered",
        key: "not covered",
        label: "Not Covered",
        value: 0,
        colors: {
          background: "rgba(214, 93, 93, 1)",
          foreground: "white",
        },
      },
      {
        id: "expired",
        key: "expired",
        label: "Expired",
        value: 0,
        colors: {
          background: "rgba(110, 196, 196, 1)",
          foreground: "white",
        },
      },
    ];

    this.assetService.getCountByCoverageStatus().subscribe(
      (count: AssetCountByCoverageStatus) => {
        this.cards = {
          ...this.cards,
          assetsChart: {
            ...this.cards.assetsChart,
            data: {
              ...this.cards.assetsChart.data,
              data: data.map((d) => ({
                ...d,
                value: count[d.key],
              })),
              colors: [
                {
                  backgroundColor: data.map((d) => d.colors.background),
                },
              ],
            },
          },
        };
      },
      () => {
        this.cards = {
          ...this.cards,
          assetsChart: {
            ...this.cards.assetsChart,
            data: {
              ...this.cards.assetsChart.data,
              data,
            },
          },
        };
      },
    );
  }

  private updateLocations(options: QueryParams<Location>): void {
    this.mapBoxService.cleanMap();
    this.mapBoxService.buildMap({
      container: "geoMap",
      style: "mapbox://styles/mapbox/streets-v11",
      zoom: 3,
      center: [-98.35, 39.5],
    });
    this.locationService
      .getLocations({
        ...options,
        where: {
          assets: [{ coverageStatus: AssetCoverageStatuses.active }, { coverageStatus: AssetCoverageStatuses.pending }],
        },
        relations: { country: true },
        distinct: true,
      })
      .pipe(
        map((data: PaginatedResponse<Location>) => {
          this.cards.locationsMap.data.dataSource = data;
          return data.items.map(
            (location: Location) =>
              ({
                id: location.id,
                name: location.name,
                house_number: location.address2,
                street: location.address1,
                postcode: location.zip,
                city: location.city,
                state: location.state,
                country: location.country.name,
              } as MapBoxLocation),
          );
        }),
      )
      .subscribe((locations: MapBoxLocation[]) => {
        locations.forEach((location) => {
          const { name, id, ...address } = location;
          this.mapBoxService
            .getAddress(address)
            .subscribe((featureCollection) => {
              if (!featureCollection?.features.length) {
                return;
              }
              const feature = featureCollection.features.reduce((r, o) => {
                r = r && r.relevance > o.relevance ? r : o;
                return r;
              }, {});
              const marker = this.mapBoxService.addMarker({
                lngLat: feature.center,
              });
              marker.setPopup(
                this.mapBoxService.createPopup({
                  html: `<a href="locations/${id}?assets_filter=coverageStatus&assets_value=active&assets_page=1&"><strong>${name}</strong></a><p>${feature.place_name}</p>`,
                }),
              );
            })
            .add(() => this.mapBoxService.fitBounds());
        });
      });
  }

  // #endregion
}
